// @flow

import * as React from 'react';

import i18n from 'app/common/i18n';
import Alert from 'app/common/alert';
import Button from 'app/common/button';
import Input from 'app/common/form/input';
import { Grid, Cell } from 'react-flexr';

import Paper from '@mui/material/Paper';

import ResetPassword from '../reset-password-modal';
import DeleteUser from '../delete-user-modal';
import UserType from '../user-type-modal';
import { date } from 'app/lib/format';
import graph from 'app/lib/graph';

function validateEmail(email) {
  return /.+@.+\..+/.test(email);
}

type Props = {
  user: { get?: (string) => string },
  onRequestUpdate: (mixed) => Promise<{ type: string, payload: mixed }>
};

type State = {
  firstname: string,
  lastname: string,
  email: string,
  phone: string,
  company?: string,
  companyAddress?: string,
  cvr?: string,
  type: string,
  uuid: string,
  changed: boolean,
  error: mixed | null
};

function getUser(user) {
  return {
    firstname: user.firstname || user.get?.('firstname') || '',
    lastname: user.lastname || user.get?.('lastname') || '',
    email: user.email || user.get?.('email') || '',
    phone: user.phone || user.get?.('phone') || '',
    uuid: user.uuid || user.get?.('uuid') || '',
    company: user.company || user.get?.('company') || '',
    companyAddress: user.companyAddress || user.get?.('companyAddress') || '',
    cvr: user.cvr || user.get?.('cvr') || '',
    type: user.type || user.get?.('type') || '',
    expireAt: user.expireAt || user.get?.('expireAt') || '',
    feedToken: user.feedToken || user.get?.('feedToken') || ''
  };
}

class Profile extends React.Component<Props, State> {
  i18n: (string) => string;

  constructor(props: Props) {
    super(props);
    const { user } = props;
    this.state = {
      ...getUser(user),
      changed: false,
      error: null
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.user !== this.props.user) {
      const { user } = nextProps;
      this.setState({
        ...getUser(user),
        changed: false,
        error: null
      });
    }
  }

  onChange = (ev) => {
    this.setState({ [ev.target.name]: ev.target.value, changed: true });
  };

  handleSubmit = async (ev) => {
    ev.preventDefault();

    const {
      firstname,
      lastname,
      email,
      phone,
      company,
      companyAddress,
      cvr
    } = this.state;

    if (!validateEmail(email)) {
      return this.setState({
        error: this.props.i18n('Please enter a valid email')
      });
    }

    try {
      await this.props.onRequestUpdate({
        firstname,
        lastname,
        email,
        phone,
        company,
        companyAddress,
        cvr
      });
      this.setState({ changed: false, error: null });
    } catch (err) {
      if (__DEV__) {
        console.error(err);
      }
      const error =
        typeof err === 'string'
          ? err
          : err.message || 'Something went wrong, please try again';
      this.setState({ error: this.props.i18n(error) });
    }
  };

  generateToken = async () => {
    const uuid = this.state.uuid;

    const res = await graph(
      'mutation  GenerateToken($uuid: ID!) { generateToken(uuid: $uuid) }',
      {
        uuid
      }
    );
    if (res.data?.generateToken) {
      this.setState({ feedToken: res.data.generateToken });
    }
  };

  reset = () => {
    const { user } = this.props;
    this.setState({
      ...getUser(user),
      changed: false,
      error: null
    });
  };

  render() {
    const {
      changed,
      error,
      firstname,
      lastname,
      email,
      phone,
      company,
      companyAddress,
      type,
      uuid,
      cvr,
      expireAt,
      feedToken
    } = this.state;

    return (
      <Paper
        component="form"
        className="profile-settings"
        onSubmit={this.handleSubmit}
        style={{ marginBottom: '1em', padding: '1em' }}
      >
        <Grid>
          <Cell size="1/2" palm="1/1">
            <Input
              fullWidth
              name="firstname"
              onChange={this.onChange}
              required
              value={firstname}
              type="text"
              label={this.props.i18n('Given name(s)')}
            />
          </Cell>
          <Cell size="1/2" palm="1/1">
            <Input
              fullWidth
              name="lastname"
              onChange={this.onChange}
              required
              value={lastname}
              type="text"
              label={this.props.i18n('Family name')}
            />
          </Cell>
          <Cell size="1/2" palm="1/1">
            <Input
              fullWidth
              name="phone"
              onChange={this.onChange}
              required
              value={phone}
              type="tel"
              label={this.props.i18n('Phone')}
            />
          </Cell>
          <Cell size="1/2" palm="1/1">
            <Input
              fullWidth
              name="email"
              onChange={this.onChange}
              required
              value={email}
              type="email"
              label={this.props.i18n('Email')}
            />
          </Cell>
          {type === 'BUSINESS' && (
            <>
              <Cell size="1/2" palm="1/1">
                <Input
                  fullWidth
                  name="company"
                  onChange={this.onChange}
                  required
                  value={company}
                  type="text"
                  label={this.props.i18n('Company')}
                />
              </Cell>
              <Cell size="1/2" palm="1/1">
                <Input
                  fullWidth
                  name="cvr"
                  onChange={this.onChange}
                  inputProps={{
                    readOnly: this.props.isAdmin ? false : true,
                    disabled: this.props.isAdmin ? false : true
                  }}
                  value={cvr}
                  type="text"
                  label={this.props.i18n('CVR')}
                />
              </Cell>
            </>
          )}
          {type === 'BUSINESS' && (
            <Cell size="1/1" palm="1/1">
              <Input
                fullWidth
                name="companyAddress"
                onChange={this.onChange}
                required
                value={companyAddress}
                type="text"
                label={this.props.i18n('Company address')}
              />
            </Cell>
          )}
          {this.props.isAdmin && (
            <Cell size="1/2" palm="1/1">
              <Input
                inputProps={{
                  readOnly: true,
                  disabled: true
                }}
                fullWidth
                name="uuid"
                type="text"
                value={uuid}
                label={this.props.i18n('UUID')}
              />
              {expireAt && (
                <div style={{ color: 'red' }}>
                  Brugeren vil blive slettet helt d.{' '}
                  {date.format(new Date(expireAt))}
                </div>
              )}
            </Cell>
          )}
          {this.props.isAdmin ? (
            <Cell
              size="1/2"
              palm="1/1"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Input
                inputProps={{
                  readOnly: true,
                  disabled: true
                }}
                fullWidth
                name="token"
                type="text"
                value={feedToken}
                label={this.props.i18n('Token')}
              />
              <Button
                key="generateToken"
                primary
                autoFocus
                size="small"
                label={this.props.i18n('Generate')}
                onClick={() => this.generateToken()}
                style={{ marginLeft: '12px' }}
              />
            </Cell>
          ) : null}
        </Grid>

        {error ? <Alert type="danger" text={error} /> : null}

        <div
          style={{ textAlign: 'right', paddingTop: '12px', display: 'inline' }}
        >
          <div
            style={{
              align: 'left'
            }}
          >
            {this.props.isAdmin ? (
              <ResetPassword
                confirmation
                user={this.props.user}
                onClick={this.props.onRequestPasswordReset}
              />
            ) : null}
            {this.props.isAdmin ? (
              <DeleteUser
                confirmation
                user={this.props.user}
                onSubmit={(user) => {
                  this.setState((state) => ({ ...state, ...user }));
                }}
              />
            ) : null}
            {this.props.isAdmin ? (
              <UserType
                confirmation
                user={this.props.user}
                onSubmit={(user) => {
                  this.setState((state) => ({ ...state, ...user }));
                  this.props.onSetUserType();
                }}
              />
            ) : null}
            {changed ? (
              <Button
                flat
                secondary
                tabIndex={-1}
                label={this.props.i18n('Reset')}
                onClick={this.reset}
                style={{ float: 'left' }}
              />
            ) : null}
          </div>

          <div
            style={{
              align: 'right'
            }}
          >
            <Button
              type="submit"
              primary
              disabled={!changed}
              label={
                changed ? this.props.i18n('Save') : this.props.i18n('Saved')
              }
            />
          </div>
        </div>
      </Paper>
    );
  }
}

export default i18n(Profile);
